import type { DirectusUser, DirectusRole } from '#shared/types/schema';

export interface AuthUser {
	id: string;
	email: string | null;
	first_name: string | null;
	last_name: string | null;
	avatar?: string | null;
	role: DirectusRole | string | null;
	status: 'draft' | 'invited' | 'unverified' | 'active' | 'suspended' | 'archived';
}

export interface ProfileAddressRow {
	id: number;
	street?: string | null;
	city?: { id?: number; name?: string | null } | null;
	zipcode?: { id?: number; locality?: string | null; zipcode?: string | null } | null;
	province?: { id?: number; name?: string | null } | null;
	region?: { id?: number; name?: string | null } | null;
	country?: { id?: number; name?: string | null } | null;
}

export interface ProfileCustomerAddress {
	id: number;
	label?: string | null;
	is_default_billing?: boolean | null;
	is_default_shipping?: boolean | null;
	address?: ProfileAddressRow | null;
}

export interface AuthCustomer {
	id?: number;
	first_name?: string | null;
	last_name?: string | null;
	phone?: string | null;
	email?: string | null;
	date_of_birth?: string | null;
	gender?: 'male' | 'female' | 'transgender' | 'agender' | 'not-specified' | null;
	addresses?: ProfileCustomerAddress[] | null;
}

const REFRESH_TOKEN_KEY = 'directus_refresh_token';

export const useAuth = () => {
	const user = useState<AuthUser | null>('auth.user', () => null);
	const customer = useState<AuthCustomer | null>('auth.customer', () => null);
	const isAuthenticated = computed(() => !!user.value);
	const isLoading = useState('auth.loading', () => false);

	const accessToken = useState<string | null>('auth.token', () => {
		if (process.client) {
			return localStorage.getItem('directus_access_token');
		}
		return null;
	});

	const refreshToken = useState<string | null>('auth.refresh_token', () => {
		if (process.client) {
			return localStorage.getItem(REFRESH_TOKEN_KEY);
		}
		return null;
	});

	function persistTokens(access: string, refresh: string) {
		if (process.client) {
			localStorage.setItem('directus_access_token', access);
			localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
			accessToken.value = access;
			refreshToken.value = refresh;
		}
	}

	function clearTokens() {
		if (process.client) {
			localStorage.removeItem('directus_access_token');
			localStorage.removeItem(REFRESH_TOKEN_KEY);
			accessToken.value = null;
			refreshToken.value = null;
		}
	}

	/** Rinnova l'access token usando il refresh token. Usato automaticamente su 401 o manualmente. */
	const refreshAccessToken = async (): Promise<boolean> => {
		const currentRefresh = process.client ? localStorage.getItem(REFRESH_TOKEN_KEY) : refreshToken.value;
		if (!currentRefresh) return false;
		try {
			const raw = await $fetch<{ access_token: string; refresh_token?: string; expires?: number }>('/api/auth/refresh', {
				method: 'POST',
				body: { refresh_token: currentRefresh },
			});
			const res = (raw as any)?.data ?? raw;
			if (res?.access_token) {
				persistTokens(res.access_token, res.refresh_token ?? currentRefresh);
				return true;
			}
		} catch {
			clearTokens();
			user.value = null;
			customer.value = null;
		}
		return false;
	};

	const login = async (email: string, password: string) => {
		isLoading.value = true;
		try {
			const rawResponse = await $fetch<any>('/api/auth/login', {
				method: 'POST',
				body: { email, password },
			});

			const response = (rawResponse as any)?.data || rawResponse;

			if (!response || !response.user) {
				throw new Error('Risposta di login non valida: dati utente mancanti');
			}

			user.value = response.user;

			if (response.access_token && process.client) {
				if (response.refresh_token) {
					persistTokens(response.access_token, response.refresh_token);
				} else {
					// Nessun refresh token (es. Directus senza refresh): salva solo access token
					localStorage.setItem('directus_access_token', response.access_token);
					accessToken.value = response.access_token;
				}
			}

			return { success: true, user: response.user };
		} catch (error: any) {
			const errorMessage = error.data?.message || error.data?.statusMessage || error.message || 'Errore durante il login';
			return {
				success: false,
				error: errorMessage,
			};
		} finally {
			isLoading.value = false;
		}
	};

	const logout = async () => {
		isLoading.value = true;
		try {
			const currentRefresh = process.client ? localStorage.getItem(REFRESH_TOKEN_KEY) : refreshToken.value;
			await $fetch('/api/auth/logout', {
				method: 'POST',
				body: currentRefresh ? { refresh_token: currentRefresh } : undefined,
			});
			user.value = null;
			customer.value = null;
			clearTokens();
			return { success: true };
		} catch (error: any) {
			user.value = null;
			customer.value = null;
			clearTokens();
			return {
				success: false,
				error: error.data?.message || error.message || 'Errore durante il logout',
			};
		} finally {
			isLoading.value = false;
		}
	};

	const fetchUser = async () => {
		isLoading.value = true;
		try {
			let token = process.client ? localStorage.getItem('directus_access_token') : accessToken.value;
			let response: { user: AuthUser; customer?: AuthCustomer } | null = null;

			const doFetch = () =>
				$fetch<{ user: AuthUser; customer?: AuthCustomer }>('/api/auth/me', {
					headers: token ? { Authorization: `Bearer ${token}` } : {},
				});

			try {
				response = await doFetch();
			} catch (err: any) {
				if (err?.statusCode === 401 && (await refreshAccessToken())) {
					token = process.client ? localStorage.getItem('directus_access_token') : accessToken.value;
					response = await doFetch();
				} else {
					throw err;
				}
			}

			if (response) {
				user.value = response.user;
				customer.value = response.customer ?? null;
				return { success: true, user: response.user };
			}

			user.value = null;
			customer.value = null;
			clearTokens();
			return { success: false, error: 'Recupero utente fallito' };
		} catch (error: any) {
			user.value = null;
			customer.value = null;
			clearTokens();
			return {
				success: false,
				error: error.data?.message || error.message || 'Errore durante il recupero utente',
			};
		} finally {
			isLoading.value = false;
		}
	};

	const requestPasswordReset = async (email: string) => {
		isLoading.value = true;
		try {
			await $fetch('/api/auth/password/request-reset', {
				method: 'POST',
				body: { email },
			});
			return { success: true };
		} catch (error: any) {
			return {
				success: false,
				error: error.data?.message || error.message || 'Errore durante la richiesta di reset password',
			};
		} finally {
			isLoading.value = false;
		}
	};

	const resetPassword = async (token: string, password: string) => {
		isLoading.value = true;
		try {
			await $fetch('/api/auth/password/reset', {
				method: 'POST',
				body: { token, password },
			});
			return { success: true };
		} catch (error: any) {
			return {
				success: false,
				error: error.data?.message || error.message || 'Errore durante il reset password',
			};
		} finally {
			isLoading.value = false;
		}
	};

	// Verifica se l'utente ha il ruolo "Cliente"
	const isCliente = computed(() => {
		if (!user.value) return false;
		const role = user.value.role;
		if (typeof role === 'object' && role !== null) {
			return role.name === 'Cliente';
		}
		return false;
	});

	// Verifica se l'utente è attivo
	const isActive = computed(() => {
		return user.value?.status === 'active';
	});

	// Verifica se l'utente può accedere all'area riservata
	const canAccessReservedArea = computed(() => {
		return isAuthenticated.value && isCliente.value && isActive.value;
	});

	return {
		user: readonly(user),
		customer: readonly(customer),
		isAuthenticated,
		isLoading: readonly(isLoading),
		accessToken: readonly(accessToken),
		refreshToken: readonly(refreshToken),
		isCliente,
		isActive,
		canAccessReservedArea,
		login,
		logout,
		fetchUser,
		refreshAccessToken,
		requestPasswordReset,
		resetPassword,
	};
};

